import React, { useState } from "react";
import { MessageSquare, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitMedFeedback } from "@/lib/api";
import { errorMessage } from "@/lib/errorMessage";

const KINDS = [
  { key: "error", label: "Something is wrong" },
  { key: "suggestion", label: "Idea / suggestion" },
  { key: "thanks", label: "This helped me" },
  { key: "other", label: "Other" },
];

export default function FeedbackForm({ context = "" }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("error");
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 5) return;
    setSubmitting(true);
    try {
      await submitMedFeedback({ kind, message: message.trim(), contact: contact.trim(), context });
      setDone(true);
      toast.success("Thanks — we read every message.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not send feedback."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        data-testid="feedback-open"
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs text-[#5C6B62] hover:text-[#2B5339] print:hidden"
      >
        <MessageSquare size={12} /> Report a mistake or send feedback
      </button>
    );
  }

  if (done) {
    return (
      <div
        data-testid="feedback-done"
        className="mt-2 inline-flex items-center gap-2 bg-[#E8F0EB] border border-[#2B5339] rounded-xl px-4 py-3 text-sm text-[#1C2B23] print:hidden"
      >
        <CheckCircle2 size={15} className="text-[#2B5339]" />
        Thanks — your message is in the queue.
      </div>
    );
  }

  return (
    <form
      data-testid="feedback-form"
      onSubmit={submit}
      className="mt-4 bg-white border border-[#E5E2DC] rounded-2xl p-5 space-y-3 max-w-xl print:hidden"
    >
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] inline-flex items-center gap-1.5">
        <MessageSquare size={11} /> Feedback · anonymous OK
      </div>
      <div className="flex flex-wrap gap-1.5">
        {KINDS.map((k) => (
          <button
            key={k.key}
            data-testid={`feedback-kind-${k.key}`}
            type="button"
            onClick={() => setKind(k.key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              kind === k.key
                ? "bg-[#2B5339] border-[#2B5339] text-white"
                : "bg-white border-[#E5E2DC] text-[#5C6B62] hover:border-[#2B5339]"
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>
      <textarea
        data-testid="feedback-message"
        required
        rows={3}
        maxLength={2000}
        placeholder="Tell us what you noticed. If you found an error in a medication card, please include the medication name."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className="w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#2B5339]"
      />
      <input
        data-testid="feedback-contact"
        type="text"
        maxLength={200}
        placeholder="Email or phone (optional — for a reply)"
        value={contact}
        onChange={(e) => setContact(e.target.value)}
        className="w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:border-[#2B5339]"
      />
      <div className="flex items-center gap-3">
        <button
          data-testid="feedback-submit"
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 bg-[#2B5339] text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-[#1C2B23] disabled:opacity-50"
        >
          {submitting && <Loader2 size={13} className="animate-spin" />}
          Send
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-[#5C6B62] hover:text-[#2B5339]"
        >
          Cancel
        </button>
      </div>
      <p className="text-[11px] text-[#5C6B62]">
        We do not include your medication data with feedback. Contact info is optional.
      </p>
    </form>
  );
}
